import { ILanguageModel, IScreenDesignInfo } from "@kuika/kuika-cl-model";
import type { Row, Workbook, Worksheet } from "exceljs";
import * as React from "react";
import { ScreenDesignTranslationService } from "../../../../services/screen-design-translation-service";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton, {
  type IKuikaButtonStatusLabels,
  type KuikaButtonStatus
} from "../../../ui-kit/kuika-button/kuika-button";
import KuikaEmptyState from "../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaFileInput from "../../../ui-kit/kuika-file-input/kuika-file-input";
import KuikaInfoBox from "../../../ui-kit/kuika-info-box/kuika-info-box";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTable, { KuikaTableColumn } from "../../../ui-kit/kuika-table/kuika-table";
import { failureMessageOr, toastService } from "../../../ui-kit/kuika-toast/toast-service";
import "./import-excel-modal.scss";

/** Import is not a save: the commit says what it is doing in its own words. */
const IMPORT_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Importing", success: "Imported" };

/** The header row an exported translation sheet must start with, in this order. */
const EXPECTED_HEADERS: string[] = ["Context", "Source Text", "Translation", "Internal ID (Do Not Modify)"];

const PARSE_ERROR_MESSAGE: string = "Failed to parse Excel file. Please ensure it's in the correct format.";

export interface IImportExcelModalProps {
  closeModal: () => void;
  showModal: boolean;
  languages: ILanguageModel[];
  selectedLanguage: ILanguageModel;
  onImportSuccess: () => void;
  screenList: IScreenDesignInfo[];
}

/** One parsed row of the imported sheet: the base-language source and its translation. */
export interface IImportExcelPreviewRow {
  /** The row's position in the sheet — the preview table's row identity. */
  rowKey: string;
  sourceText: string;
  translation: string;
  tagName?: string;
  screenName?: string;
}

/** What the hidden `_Data` sheet records for one context: the real source text and where it lives. */
interface IImportExcelDataSheetEntry {
  sourceText: string;
  tagName: string;
  screenName: string;
}

/** A parsed workbook: the rows that carry a translation, and how many data rows the sheet had in all. */
export interface IImportExcelParseResult {
  rows: IImportExcelPreviewRow[];
  totalRowCount: number;
}

export interface IImportExcelModalState {
  isParsing: boolean;
  isImporting: boolean;
  isDownloadingTemplate: boolean;
  selectedFile: File | null;
  previewData: IImportExcelPreviewRow[] | null;
  sheetRowCount: number;
  fileParseError: string | null;
  missingScreens: string[];
  showMissingScreensWarning: boolean;
  /** Where Import stands. Owned by `SaveStatus`, never set directly. */
  importStatus: KuikaButtonStatus;
}

/** A worksheet's rows as strings, 0-indexed (ExcelJS `row.values` is 1-indexed). */
const readSheetRows = (sheet: Worksheet): string[][] => {
  const rows: string[][] = [];
  sheet.eachRow((row: Row): void => {
    const values: (string | undefined)[] = row.values as (string | undefined)[];
    rows.push(values.slice(1).map((value: string | undefined): string => (value != null ? String(value) : "")));
  });
  return rows;
};

/** "1 row", "12 rows". */
const formatRowCount = (count: number): string => `${count} ${count === 1 ? "row" : "rows"}`;

export class ImportExcelModal extends React.PureComponent<IImportExcelModalProps, IImportExcelModalState> {
  /**
   * Import's state machine: the ✓ is held on Import while the translations behind the dialog
   * reload, then the dialog closes onto them. `canStart` keeps a second import out meanwhile.
   */
  private readonly importStatus: SaveStatus = new SaveStatus((importStatus: KuikaButtonStatus): void => {
    this.setState({ importStatus });
  });

  constructor(props: IImportExcelModalProps) {
    super(props);
    this.state = {
      isParsing: false,
      isImporting: false,
      isDownloadingTemplate: false,
      selectedFile: null,
      previewData: null,
      sheetRowCount: 0,
      fileParseError: null,
      missingScreens: [],
      showMissingScreensWarning: false,
      importStatus: "idle"
    };
  }

  componentWillUnmount(): void {
    this.importStatus.dispose();
  }

  parseExcelFile = async (file: File): Promise<IImportExcelParseResult> => {
    const ExcelJS: typeof import("exceljs") = await import("exceljs");
    const arrayBuffer: ArrayBuffer = await file.arrayBuffer();

    const workbook: Workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(arrayBuffer);

    const translationsSheet: Worksheet | undefined = workbook.getWorksheet("Translations");
    if (!translationsSheet) {
      throw new Error("Invalid template format: 'Translations' sheet not found");
    }

    const sheetRows: string[][] = readSheetRows(translationsSheet);

    if (sheetRows.length < 2) {
      throw new Error("Excel template contains no data");
    }

    const headerRow: string[] = sheetRows[0];
    if (
      headerRow.length < EXPECTED_HEADERS.length ||
      !EXPECTED_HEADERS.every((header: string, i: number): boolean => headerRow[i] === header)
    ) {
      throw new Error(`Invalid template header. Expected: ${EXPECTED_HEADERS.join(", ")}`);
    }

    // The hidden Data sheet holds the actual source text values, keyed by context.
    const dataSheetValues: Map<string, IImportExcelDataSheetEntry> = new Map();
    const dataSheet: Worksheet | undefined = workbook.getWorksheet("_Data");
    if (dataSheet) {
      const dataSheetRows: string[][] = readSheetRows(dataSheet);

      for (let i = 1; i < dataSheetRows.length; i++) {
        const row: string[] = dataSheetRows[i];
        if (row && row[0]) {
          const context: string = row[0];
          const sourceText: string = row[1] ?? "";

          let tagName: string = "";
          let screenName: string = "";

          // Extract screenName and tagName from context (format is typically "ScreenName - TagName" or variants)
          const parts: string[] = context.split(" - ");
          if (parts.length > 1) {
            screenName = parts[0].trim();
            const secondPart: string = parts[1];
            if (secondPart.includes(":")) {
              tagName = secondPart.split(":")[1].trim();
            } else {
              tagName = secondPart.trim();
            }
          }

          dataSheetValues.set(context, { sourceText, tagName, screenName });
        }
      }
    }

    const dataRows: string[][] = sheetRows.slice(1);
    const rows: IImportExcelPreviewRow[] = dataRows.reduce(
      (acc: IImportExcelPreviewRow[], row: string[], index: number): IImportExcelPreviewRow[] => {
        const context: string = row[0] ? row[0].trim() : "";
        const dataEntry: IImportExcelDataSheetEntry | undefined = context ? dataSheetValues.get(context) : undefined;
        const translation: string = row[2] ? row[2].trim() : "";

        // An empty Translation cell leaves the existing translation untouched, so only rows that carry one are previewed.
        if (translation === "") {
          return acc;
        }

        if (dataEntry) {
          acc.push({
            rowKey: `row-${index}`,
            sourceText: dataEntry.sourceText,
            translation,
            tagName: dataEntry.tagName,
            screenName: dataEntry.screenName
          });
        } else {
          // Fall back to the value in the sheet's own Source Text cell.
          acc.push({ rowKey: `row-${index}`, sourceText: row[1] ? row[1].trim() : "", translation });
        }
        return acc;
      },
      []
    );

    return { rows, totalRowCount: dataRows.length };
  };

  /** Screens the file names that this project does not have, in the order the file first names them. */
  getMissingScreens = (rows: IImportExcelPreviewRow[]): string[] => {
    const existingScreenNames: Set<string> = new Set(
      this.props.screenList.map((screen: IScreenDesignInfo): string => screen.name)
    );
    const missingScreens: Set<string> = new Set();

    for (const row of rows) {
      if (row.screenName && !existingScreenNames.has(row.screenName)) {
        missingScreens.add(row.screenName);
      }
    }

    return Array.from(missingScreens);
  };

  handleFileSelect = async (file: File | null): Promise<void> => {
    if (!file) {
      this.setState({
        selectedFile: null,
        isParsing: false,
        previewData: null,
        sheetRowCount: 0,
        fileParseError: null,
        missingScreens: []
      });
      return;
    }

    this.setState({
      selectedFile: file,
      isParsing: true,
      previewData: null,
      sheetRowCount: 0,
      fileParseError: null,
      missingScreens: []
    });

    try {
      const result: IImportExcelParseResult = await this.parseExcelFile(file);

      // Cleared (or replaced) while the workbook was being read: that answer is no longer wanted.
      if (this.state.selectedFile !== file) {
        return;
      }

      this.setState({
        previewData: result.rows,
        sheetRowCount: result.totalRowCount,
        missingScreens: this.getMissingScreens(result.rows),
        isParsing: false
      });
    } catch (error) {
      if (this.state.selectedFile !== file) {
        return;
      }

      console.error("Error parsing Excel file:", error);
      this.setState({ isParsing: false, fileParseError: PARSE_ERROR_MESSAGE, previewData: null });
      KMainFunctions.exceptionHandler(error);
    }
  };

  /**
   * The template is built on the server before it can be saved, so one toast carries it from
   * "Preparing" to its outcome; the button beside it keeps its own spinner meanwhile.
   */
  handleDownloadTemplate = async (): Promise<void> => {
    const languageName: string = this.props.selectedLanguage.name;
    this.setState({ isDownloadingTemplate: true });

    const work: Promise<void> = (async (): Promise<void> => {
      const blob: Blob = await ScreenDesignTranslationService.exportExcelTemplate(languageName);

      const url: string = window.URL.createObjectURL(blob);
      const link: HTMLAnchorElement = document.createElement("a");
      link.href = url;
      link.download = `${languageName.toLowerCase()}_translations.xlsx`;
      document.body.appendChild(link);
      link.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    })();
    toastService.promise(work, {
      loading: "Preparing the template…",
      success: "Template downloaded",
      error: failureMessageOr("The template could not be downloaded.")
    });

    try {
      await work;
    } catch {
      // The toast has said why.
    } finally {
      this.setState({ isDownloadingTemplate: false });
    }
  };

  handleImport = async (): Promise<void> => {
    if (!this.state.selectedFile) {
      KMainFunctions.displayWarningNotification("Please select a file to import");
      return;
    }

    // Rows for screens this project does not have are skipped: say so before importing.
    if (this.state.missingScreens.length > 0) {
      this.setState({ showMissingScreensWarning: true });
      return;
    }

    await this.proceedWithImport();
  };

  proceedWithImport = async (): Promise<void> => {
    const file: File | null = this.state.selectedFile;
    if (!file) {
      return;
    }

    if (!this.importStatus.canStart) {
      return;
    }

    const language: ILanguageModel = this.props.selectedLanguage;
    this.importStatus.start();
    this.setState({ isImporting: true });

    try {
      const isImported: boolean = await ScreenDesignTranslationService.importFromExcel(
        file,
        language.id,
        language.name,
        language.shortName,
        language.isDefault
      );

      this.setState({ isImporting: false });

      if (isImported) {
        // The translations behind the dialog reload while Import holds its ✓; then it closes.
        this.props.onImportSuccess();
        this.importStatus.succeed(this.resetAndClose);
      } else {
        this.importStatus.fail();
        KMainFunctions.displayErrorNotification("Failed to import translations");
      }
    } catch (error) {
      this.setState({ isImporting: false });
      this.importStatus.fail();
      KMainFunctions.exceptionHandler(error);
    }
  };

  getBaseLanguage = (): string => {
    if (this.props.selectedLanguage.isDefault) {
      return "Default";
    }

    const defaultLanguage: ILanguageModel | undefined = this.props.languages.find(
      (language: ILanguageModel): boolean => language.isDefault
    );

    if (defaultLanguage) {
      return defaultLanguage.shortName;
    }

    return "";
  };

  getSourceColumnHeader = (): string => {
    const baseLanguage: string = this.getBaseLanguage();
    return baseLanguage ? `Source · ${baseLanguage}` : "Source";
  };

  /** The dialog's own way out: the close button, Escape and Cancel. Held while an import is running. */
  onClose = (): void => {
    // Nor while Import is holding its ✓: the dialog closes itself once it has been seen.
    if (this.state.isImporting || !this.importStatus.canStart) {
      return;
    }

    this.resetAndClose();
  };

  resetAndClose = (): void => {
    this.setState({
      isParsing: false,
      selectedFile: null,
      previewData: null,
      sheetRowCount: 0,
      fileParseError: null,
      missingScreens: [],
      showMissingScreensWarning: false
    });
    this.props.closeModal();
  };

  closeWarningModal = (): void => {
    this.setState({ showMissingScreensWarning: false });
  };

  confirmImportDespiteWarning = async (): Promise<void> => {
    this.setState({ showMissingScreensWarning: false });
    await this.proceedWithImport();
  };

  getCanImport = (): boolean => {
    const { previewData, isParsing } = this.state;
    return !isParsing && previewData !== null && previewData.length > 0;
  };

  renderCellText = (text: string): React.ReactNode => {
    if (!text) {
      return <span className="kuika-import-excel__cell-text kuika-import-excel__cell-text--empty">—</span>;
    }

    return <span className="kuika-import-excel__cell-text">{text}</span>;
  };

  renderSourceCell = (row: IImportExcelPreviewRow): React.ReactNode => {
    const hasMeta: boolean = Boolean(row.screenName || row.tagName);

    return (
      <div className="kuika-import-excel__source">
        {this.renderCellText(row.sourceText)}
        {hasMeta && (
          <div className="kuika-import-excel__source-meta">
            {row.screenName && <KuikaBadge color="grey" variant="subtle" size="small" content={row.screenName} />}
            {row.tagName && <KuikaBadge color="grey" variant="subtle" size="small" content={row.tagName} />}
          </div>
        )}
      </div>
    );
  };

  getPreviewColumns = (): KuikaTableColumn<IImportExcelPreviewRow>[] => {
    return [
      {
        id: "sourceText",
        header: (): React.ReactNode => this.getSourceColumnHeader(),
        cell: (row: IImportExcelPreviewRow): React.ReactNode => this.renderSourceCell(row)
      },
      {
        id: "translation",
        header: (): React.ReactNode => "Translation",
        cell: (row: IImportExcelPreviewRow): React.ReactNode => this.renderCellText(row.translation)
      }
    ];
  };

  renderPreview = (): React.ReactNode => {
    const { selectedFile, previewData, isParsing, sheetRowCount } = this.state;

    if (!selectedFile) {
      return (
        <KuikaEmptyState
          variant="page"
          svgName="file-type-xls"
          customName="No file selected"
          description="Rows from the file will be listed here before you import."
        />
      );
    }

    if (isParsing) {
      return (
        <KuikaTable<IImportExcelPreviewRow>
          data={[]}
          columns={this.getPreviewColumns()}
          rowKey={(row: IImportExcelPreviewRow): string => row.rowKey}
          enablePagination={false}
          fitContentHeight
          loading
        />
      );
    }

    // A file that failed to parse says why above; there is nothing to preview.
    if (previewData === null) {
      return null;
    }

    return (
      <div className="kuika-import-excel__preview">
        <span className="kuika-import-excel__summary">
          {`${formatRowCount(sheetRowCount)} · ${previewData.length} with translation`}
        </span>
        <KuikaTable<IImportExcelPreviewRow>
          data={previewData}
          columns={this.getPreviewColumns()}
          rowKey={(row: IImportExcelPreviewRow): string => row.rowKey}
          enablePagination={false}
          fitContentHeight
          emptyTitle="No translations in this file"
          emptyDescription="Every Translation cell is empty, so importing it would change nothing."
          data-testid="importExcelPreviewTable"
        />
      </div>
    );
  };

  render(): React.ReactNode {
    const { selectedFile, isParsing, isImporting, isDownloadingTemplate, fileParseError, missingScreens } = this.state;

    return (
      <>
        <KModal
          showModal={this.props.showModal}
          closeModal={this.onClose}
          header="Import Excel"
          width={880}
          className="kuika-import-excel"
          isLoading={isImporting}
          loadingLabel="Importing translations…"
          footer={
            <KModalFooter
              onCancel={this.onClose}
              submitLabel="Import"
              submitTestId="importExcelImportButton"
              onSubmit={this.handleImport}
              disabled={!this.getCanImport()}
              status={this.state.importStatus}
              statusLabels={IMPORT_STATUS_LABELS}
            />
          }
        >
          <div className="kuika-import-excel__body">
            <p className="kuika-import-excel__intro">
              Upload an Excel file exported from this dialog or the template. Translations in the file are added or
              updated; empty cells leave existing translations untouched.
            </p>

            <div className="kuika-import-excel__actions">
              <KuikaFileInput
                className="kuika-import-excel__file-input"
                accept=".xlsx"
                emptyLabel="Choose .xlsx file"
                valueLabel={selectedFile?.name}
                busy={isParsing}
                disabled={isImporting}
                onChange={this.handleFileSelect}
              />
              <KuikaButton
                type="secondary"
                color="neutral"
                loading={isDownloadingTemplate}
                onClick={this.handleDownloadTemplate}
                data-testid="importExcelDownloadTemplateButton"
              >
                <KuikaSvg name="download" width={16} height={16} />
                Download template
              </KuikaButton>
            </div>

            {fileParseError && (
              <KuikaInfoBox
                color="red"
                leftUnit={
                  <span className="kuika-import-excel__error">
                    <KuikaSvg name="alert-triangle" width={16} height={16} />
                    {fileParseError}
                  </span>
                }
              />
            )}

            {this.renderPreview()}
          </div>
        </KModal>

        <KModal
          showModal={this.state.showMissingScreensWarning}
          closeModal={this.closeWarningModal}
          header="Missing screens"
          width={480}
          className="kuika-import-excel-missing-screens"
          footer={
            <KModalFooter
              onCancel={this.closeWarningModal}
              submitLabel="Import anyway"
              submitTestId="importExcelImportAnywayButton"
              onSubmit={this.confirmImportDespiteWarning}
            />
          }
        >
          <div className="kuika-import-excel-missing-screens__body">
            <p className="kuika-import-excel-missing-screens__text">
              These screens are in the file but not in this project. Their rows will be skipped; everything else is
              imported.
            </p>
            <div className="kuika-import-excel-missing-screens__list">
              {missingScreens.map((screenName: string): React.ReactNode => (
                <KuikaBadge key={screenName} color="grey" variant="subtle" size="small" content={screenName} />
              ))}
            </div>
          </div>
        </KModal>
      </>
    );
  }
}
